import React, { useEffect, useState } from "react";
import { api } from "../api";
import { GraduationCap, Clock } from "lucide-react";

function initials(name = "") {
    return name.replace("Dr. ", "").split(" ").map((w) => w[0]).slice(0, 2).join("");
}

export function MemberCard({ m }) {
    return (
        <article className="card-glass hover-float p-7 flex flex-col items-center text-center" style={{ borderRadius: 20 }} data-testid={`board-member-${m.id}`}>
            <div className="w-28 h-28 rounded-full overflow-hidden bg-[#e8ddd2] mb-4 ring-4 ring-white/70 shadow-md grid place-items-center shrink-0">
                {m.image_url ? (
                    <img src={m.image_url} alt={m.name} className="w-full h-full object-cover" />
                ) : (
                    <span className="font-serif text-2xl text-[var(--taupe)]">{initials(m.name)}</span>
                )}
            </div>
            <h3 className="font-serif text-[21px] text-[var(--ink)] leading-tight">{m.name}</h3>
            {m.role_title && <div className="text-[12.5px] text-[var(--taupe-deep)] font-medium mt-0.5">{m.role_title}</div>}
            <div className="overline mt-2">{m.specialty}</div>
            {m.credentials && (
                <span className="mt-2 inline-block text-[11px] px-3 py-1 rounded-full bg-[var(--bg-alt)] text-[var(--ink-soft)] tracking-wide">
                    {m.credentials}
                </span>
            )}
            {m.bio && <p className="text-[13.5px] text-[var(--body)] leading-relaxed mt-3">{m.bio}</p>}
            {(m.education || m.experience) && (
                <div className="mt-4 pt-4 border-t border-[var(--line-soft)] w-full space-y-1.5 text-left">
                    {m.education && (
                        <div className="flex items-start gap-2 text-[12.5px] text-[var(--ink-soft)]">
                            <GraduationCap size={14} className="mt-0.5 shrink-0 text-[var(--taupe)]" />
                            <span>{m.education}</span>
                        </div>
                    )}
                    {m.experience && (
                        <div className="flex items-start gap-2 text-[12.5px] text-[var(--ink-soft)]">
                            <Clock size={14} className="mt-0.5 shrink-0 text-[var(--taupe)]" />
                            <span>{m.experience}</span>
                        </div>
                    )}
                </div>
            )}
        </article>
    );
}

export default function TheBoard() {
    const [members, setMembers] = useState(null);

    useEffect(() => {
        api.get("/doctors").then((r) => setMembers(r.data || [])).catch(() => setMembers([]));
    }, []);

    // The Board shows only the Core Team; other sections live on the Consult a Doctor page.
    const coreTeam = (members || []).filter((m) => m.section === "Our Core Team");

    return (
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-14" data-testid="board-page">
            <div className="text-center mb-16">
                <p className="eyebrow">The minds behind every formula</p>
                <h1 className="h-display text-[44px] sm:text-[64px] mt-4">The Board</h1>
                <p className="text-[var(--body)] max-w-xl mx-auto mt-4">
                    A standing council of practitioners and leaders that reviews every UCare formulation against the latest peer-reviewed evidence.
                </p>
            </div>

            {members === null && (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
                    {[...Array(3)].map((_, i) => <div key={i} className="card-glass h-72" style={{ borderRadius: 20 }} />)}
                </div>
            )}

            {members !== null && coreTeam.length === 0 && (
                <p className="text-center text-[var(--body)] py-16" data-testid="board-empty">Our board is being assembled — check back soon.</p>
            )}

            {coreTeam.length > 0 && (
                <section className="mb-16" data-testid="board-section-our-core-team">
                    <div className="flex items-center gap-4 mb-8">
                        <h2 className="font-serif text-[26px] sm:text-[32px] text-[var(--ink)] whitespace-nowrap">Our Core Team</h2>
                        <span className="flex-1 h-px bg-[var(--line)]" />
                        <span className="text-[11px] uppercase tracking-[0.18em] text-[var(--ink-soft)]">{coreTeam.length} member{coreTeam.length > 1 ? "s" : ""}</span>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {coreTeam.map((m) => <MemberCard key={m.id} m={m} />)}
                    </div>
                </section>
            )}
        </div>
    );
}
